import React, { useEffect, useMemo, useState } from 'react';
import useUser from 'hooks/useUser';
import { useMutation, useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { datadogRum } from '@datadog/browser-rum';
import DataDogLogs from 'shared/logging/DataDogLogs';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { getWelcomeChecklist, useVerifyAchData } from 'api/agreement';
import {
  AnswerNetVideoTPVStatusType,
  MilestoneType,
  WelcomeChecklistResult,
  WelcomeChecklistStatusType,
} from 'modules/types';
import { ErrorBoundary } from 'shared/components/ErrorBoundary/ErrorBoundary';
import { SupportForm } from 'modules/support/components/SupportForm/SupportForm';
import { BillingAddressType, FlagKey } from 'api/types';
import { getMonitoringSystemInfo } from 'modules/system/utils/getMonitoringSystemInfo';
import { useSearchParams } from 'react-router-dom';
import { updateUserBillingAddress, updateUserBillingAddressConfirmation } from 'api/user';
import { toast } from 'shared/components/Toast/Toast';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Header } from 'shared/components/Header/Header';
import {
  Body,
  ProjectContainer,
  StatusContainer,
  StatusHeaderWrapper,
  SupportFormWrapper,
} from './Status.styles';
import { ProjectStatus } from '../components/ProjectStatus/ProjectStatus';
import { StatusAccordion } from '../components/StatusAccordion/StatusAccordion';
import { shouldLoadWelcomeChecklist } from '../utils/shouldLoadWelcomeChecklist';
import { WelcomeChecklist } from './WelcomeChecklist/WelcomeChecklist';
import {
  perSessionAchDiscountModalM0,
  perSessionAchDiscountModalM2,
} from '../../../constants/localStorageKeys';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { AchModalEntryPoint, trackAchModalOpened } from '../utils/trackAchModalEvent';
import {
  EarlyAccessModals,
  EarlyAccessModalType,
} from '../components/EarlyAccessModals/EarlyAccessModals';
import { AchEnrollmentBanner } from '../components/AchEnrollmentBanner/AchEnrollmentBanner';
import { isWelcomeChecklistPassed } from '../utils/getWelcomeChecklistStatus';
import { SystemDetails } from '../components/SystemDetails/SystemDetails';

export function Status() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { userResult, selectedAgreement, refetch: refetchUser } = useUser();
  const [tpvStatus, setTpvStatus] = useState<AnswerNetVideoTPVStatusType | undefined>(undefined);
  const [attemptedAchVerification, setAttemptedAchVerification] = useState(false);

  const handleTpvStatusUpdate = (status: AnswerNetVideoTPVStatusType | undefined) => {
    setTpvStatus(status);
  };

  const {
    flags: { [FlagKey.achModal]: achModalEnabled },
  } = useFeatureFlags();

  const [achFormCompletionTime, setAchFormCompletionTime] = useState<number | null>(null);
  const [showEntryPointInSystemDetails, setShowEntryPointInSystemDetails] = useState(false);
  const [addressDisplay, setAddressDisplay] = useState<BillingAddressType | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState<EarlyAccessModalType | null>(null);

  const isAchRedirect = Boolean(searchParams.get('isAchRedirect'));
  const user = userResult?.user;
  const isLoading = !userResult || userResult.loading;
  const { system } = useMemo(() => getMonitoringSystemInfo(selectedAgreement), [selectedAgreement]);
  const isEarlyAccess = Boolean(selectedAgreement);
  const currentMilestone = selectedAgreement?.current_milestone || MilestoneType.IN_SERVICE;
  let modalKey: any;
  if (currentMilestone === MilestoneType.CONTRACT_SIGNED) modalKey = perSessionAchDiscountModalM0;
  if (currentMilestone === MilestoneType.INSTALLATION_COMPLETED)
    modalKey = perSessionAchDiscountModalM2;

  const displayChangeBillingAddress = user?.address !== system?.address && !user?.address.confirmed;
  const actionRequiredFlag =
    displayChangeBillingAddress ||
    (selectedAgreement?.is_tpv_installer &&
      (tpvStatus === undefined || tpvStatus === AnswerNetVideoTPVStatusType.PENDING) &&
      !isWelcomeChecklistPassed(selectedAgreement?.welcome_checklist_status) &&
      currentMilestone === MilestoneType.CONTRACT_SIGNED) ||
    (selectedAgreement?.is_tpv_installer &&
      currentMilestone === MilestoneType.CONTRACT_SIGNED &&
      tpvStatus === AnswerNetVideoTPVStatusType.FAILED);
  const welcomeChecklists = selectedAgreement ? selectedAgreement.welcome_checklists : [];
  const hasCompletedWelcomeChecklist =
    welcomeChecklists.length > 0 &&
    welcomeChecklists[welcomeChecklists.length - 1].result === WelcomeChecklistResult.PASS;

  const { data: welcomeChecklistQuestions, isFetching: isFetchingWelcomeChecklist } = useQuery(
    ['welcomeChecklist', selectedAgreement, hasCompletedWelcomeChecklist],
    () => {
      if (selectedAgreement) {
        return getWelcomeChecklist(selectedAgreement?.id).then((res) => res.data);
      }
      return undefined;
    },
    {
      enabled: selectedAgreement && shouldLoadWelcomeChecklist(selectedAgreement),
      refetchOnWindowFocus: false,
      onError: (error: AxiosError) => {
        DataDogLogs.error(
          'Status',
          {
            ...(userResult?.user ? { user: userResult?.user } : {}),
            selectedAgreement,
            additionalInfo: {
              hasCompletedWelcomeChecklist,
              shouldLoadWelcomeChecklist: selectedAgreement
                ? shouldLoadWelcomeChecklist(selectedAgreement)
                : 'selectedAgreement is undefined',
            },
          },
          error,
        );
      },
    },
  );

  const { mutate: mutateBillingAddress } = useMutation({
    mutationFn: updateUserBillingAddress,
    onSuccess: async () => {
      toast({
        type: 'success',
        title: t('toast.success'),
        message: t('confirmBillingAddress.success'),
        theme,
      });
      refetchUser();
      setOpenModal(null);
    },
    onError: () => {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('confirmBillingAddress.error'),
        theme,
      });
      setAddressDisplay(undefined);
    },
  });

  const { mutate: mutateBillingAddressConfirmation } = useMutation({
    mutationFn: updateUserBillingAddressConfirmation,
    onSuccess: async () => {
      toast({
        type: 'success',
        title: t('toast.success'),
        message: t('confirmBillingAddress.success'),
        theme,
      });
      refetchUser();
    },
    onError: () => {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('confirmBillingAddress.error'),
        theme,
      });
      setAddressDisplay(undefined);
    },
  });

  const { mutateAsync: verifyAchDataAsync } = useVerifyAchData({ onSuccess: refetchUser });

  // attempt to check ACH verification status if we're at M0 and were
  // not able to retrieve ACH info during import
  useEffect(() => {
    const asyncEffect = async () => {
      if (selectedAgreement && !attemptedAchVerification) {
        const {
          ach_elected: achElected,
          ach_verified: achVerified,
          id: agreementId,
        } = selectedAgreement;
        if (
          currentMilestone === MilestoneType.CONTRACT_SIGNED &&
          achElected !== false &&
          achVerified === null
        ) {
          await verifyAchDataAsync({ agreement_id: agreementId, fetch_bank_details: true });
          // prevents multiple attempts if we're unable to complete the verification process
          setAttemptedAchVerification(true);
        }
      }
    };
    asyncEffect();
  }, [selectedAgreement, verifyAchDataAsync, currentMilestone, attemptedAchVerification]);

  const showDWC =
    !hasCompletedWelcomeChecklist &&
    selectedAgreement &&
    !selectedAgreement.is_tpv_installer &&
    welcomeChecklistQuestions;

  const handleOpenAutoPaySystemDetails = () => {
    setOpenModal(EarlyAccessModalType.AUTOPAY);
    setShowEntryPointInSystemDetails(true);
    trackAchModalOpened(AchModalEntryPoint.PAYMENT_METHOD);
    datadogRum.addAction('ach-enrollment-card-clicked');
  };

  // should display the autopay modal for m0 and m2 agreements only
  const handleShowWelcomeModal = () => {
    if (modalKey && (modalKey === perSessionAchDiscountModalM2 || selectedAgreement?.ach_elected))
      setOpenModal(EarlyAccessModalType.AUTOPAY);
  };

  const handleCloseWelcomeChecklist = async (welcomeChecklistResult?: WelcomeChecklistResult) => {
    await refetchUser();
    setOpenModal(EarlyAccessModalType.CHECKLIST);

    if (welcomeChecklistResult === WelcomeChecklistResult.PASS) {
      trackAchModalOpened(AchModalEntryPoint.COMPLETED_DWC);
      handleShowWelcomeModal();
    }
  };

  const achBanner = (
    <AchEnrollmentBanner
      achFormCompletionTime={achFormCompletionTime}
      onOpenModal={setOpenModal}
      selectedAgreement={selectedAgreement}
      achModalEnabled={achModalEnabled}
      isEarlyAccess={isEarlyAccess}
      isAchRedirect={isAchRedirect}
    />
  );

  return (
    <>
      <StatusContainer>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {user && (
              <StatusHeaderWrapper>
                <Header pageName="STATUS" title={t('earlyAccess.hello')} />
                {achBanner}
              </StatusHeaderWrapper>
            )}

            <Body>
              <ProjectContainer>
                <ProjectStatus
                  currentMilestone={isEarlyAccess ? currentMilestone : 'CANCELED'}
                  actionRequiredFlag={actionRequiredFlag}
                  agreement={selectedAgreement}
                  welcomeChecklistStatus={
                    selectedAgreement?.welcome_checklist_status ||
                    WelcomeChecklistStatusType.NOT_REQUIRED
                  }
                />

                {isEarlyAccess && user ? (
                  <StatusAccordion
                    currentMilestone={currentMilestone}
                    agreement={selectedAgreement}
                    user={user}
                    isFetchingWelcomeChecklist={isFetchingWelcomeChecklist}
                    onOpenWelcomeChecklist={() => {
                      setOpenModal(EarlyAccessModalType.CHECKLIST);
                    }}
                    displayChangeBillingAddress={displayChangeBillingAddress}
                    onConfirmBillingAddress={() => {
                      setOpenModal(EarlyAccessModalType.BILLING_ADDRESS);
                    }}
                    onOpenTpv={() => {
                      setOpenModal(EarlyAccessModalType.TPV);
                    }}
                    onTpvStatusUpdate={handleTpvStatusUpdate}
                  />
                ) : (
                  <SupportFormWrapper>
                    <SupportForm email={userResult.user?.email || ''} />
                  </SupportFormWrapper>
                )}
              </ProjectContainer>

              {selectedAgreement && isEarlyAccess && (
                <SystemDetails
                  agreement={selectedAgreement}
                  onOpenAutoPay={handleOpenAutoPaySystemDetails}
                />
              )}
            </Body>
          </>
        )}
        {showDWC && (
          <ErrorBoundary>
            <WelcomeChecklist
              agreement={selectedAgreement}
              checklistQuestions={welcomeChecklistQuestions}
              isOpen={openModal === EarlyAccessModalType.CHECKLIST}
              onCloseWelcomeChecklist={handleCloseWelcomeChecklist}
            />
          </ErrorBoundary>
        )}
      </StatusContainer>
      <EarlyAccessModals
        modalKey={modalKey}
        openModal={openModal}
        onSetOpenModal={setOpenModal}
        addressDisplay={addressDisplay}
        onSetAddressDisplay={setAddressDisplay}
        onSetAchFormCompletionTime={setAchFormCompletionTime}
        onResetSearchParams={() => setSearchParams()}
        showEntryPointInSystemDetails={showEntryPointInSystemDetails}
        onSetShowEntryPointInSystemDetails={setShowEntryPointInSystemDetails}
        hasAchBanner={!!achBanner}
        isAchRedirect={isAchRedirect}
        achModalEnabled={achModalEnabled}
        mutateBillingAddress={mutateBillingAddress}
        mutateBillingAddressConfirmation={mutateBillingAddressConfirmation}
      />
    </>
  );
}

export default Status;
